import React, { useState, useCallback, useRef, useEffect } from 'react';
import { usePlaceContext } from '../itinerary/PlaceContext';
import { clearColumnMarkers, addSingleMarker } from './MarkerUtils';
import '../../styles/components/map_interaction/MapSearchBar.css';
import { debounce } from 'lodash';
import CreateCustomPlaceWidget from '../itinerary/custom_place/CreateCustomPlaceWidget';

const MapSearchBar = ({ fetchPlaceDetailsWrapper, googleMapsAPI, googleMap, placesService }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { markersRef, showPlaceInfoBox } = usePlaceContext();
  const [showCreateCustom, setShowCreateCustom] = useState(false);
  const [isCreatingCustom, setIsCreatingCustom] = useState(false);
  const [customMarker, setCustomMarker] = useState(null);
  const [customCompositeKey, setCustomCompositeKey] = useState(null);
  const [customLatLng, setCustomLatLng] = useState(null);
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [customButtonHovered, setCustomButtonHovered] = useState(false);
  const mapSearchBarRef = useRef(null);

  useEffect(() => {
    const checkIsMobile = () => {
      const ua = navigator.userAgent;
      if (/Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    };
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const nearbySearchAsync = useCallback((request) => {
    return new Promise((resolve, reject) => {
      placesService.nearbySearch(request, (results, status) => {
        if (status === googleMapsAPI.places.PlacesServiceStatus.OK) {
          resolve(results);
        } else {
          reject(`Nearby search failed: ${status}`);
        }
      });
    });
  }, [placesService, googleMapsAPI]);

  const performSearch = useCallback(async (request) => {
    try {
      const results = await nearbySearchAsync(request);
      clearColumnMarkers('search', markersRef);
      for (const place of results) {
        addSingleMarker(
          googleMapsAPI,
          googleMap,
          {
            place_id: place.place_id,
            name: place.name,
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
            types: [],
          },
          showPlaceInfoBox,
          markersRef,
          'blue',
          'search',
          fetchPlaceDetailsWrapper,
          placesService,
          true,
          'suggestion'
        );
      }
    } catch (error) {
      console.error(error);
    }
  }, [nearbySearchAsync, googleMapsAPI, googleMap, markersRef, showPlaceInfoBox, fetchPlaceDetailsWrapper, placesService]);

  const handleKeywordSearch = useCallback(debounce(async (e) => {
    e.preventDefault();
    if (!placesService || !searchTerm || !googleMap) return;
    const center = googleMap.getCenter();
    const bounds = googleMap.getBounds();
    const request = {
      location: new googleMapsAPI.LatLng(center.lat(), center.lng()),
      bounds: bounds,
      keyword: searchTerm,
      fields: ['place_id', 'name', 'geometry.location'],
      rankBy: googleMapsAPI.places.RankBy.PROMINENCE,
    };
    await performSearch(request);
  }, 200), [googleMap, placesService, searchTerm, performSearch, googleMapsAPI]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handleKeywordSearch(e);
    }
  }, [handleKeywordSearch]);

  const handleClearSearchMarkers = useCallback(debounce(() => {
    clearColumnMarkers('search', markersRef);
    setSearchTerm('');
  }, 200), [markersRef]);

  const handleButtonClick = useCallback((keyword) => {
    setSearchTerm(keyword);
    const perform = async () => {
      if (!placesService || !googleMap) return;
      const center = googleMap.getCenter();
      const bounds = googleMap.getBounds();
      const request = {
        location: new googleMapsAPI.LatLng(center.lat(), center.lng()),
        bounds: bounds,
        keyword: keyword,
        fields: ['place_id', 'name', 'geometry.location'],
        rankBy: googleMapsAPI.places.RankBy.PROMINENCE,
      };
      await performSearch(request);
    };
    perform();
  }, [placesService, googleMap, performSearch, googleMapsAPI]);

  const handleCustomButtonClick = () => {
    if (isCustomMode) {
      cancelCustomMode();
      return;
    }
    if (!googleMap || !googleMapsAPI) return;
    setIsCreatingCustom(true);
    setIsCustomMode(true);
    googleMap.setOptions({ draggableCursor: 'crosshair' });
    const mouseMoveListener = googleMap.addListener('mousemove', (e) => {
      if (customMarker) customMarker.setPosition(e.latLng);
    });
    let clickListener;
    const globalClickListener = (event) => {
      if (mapSearchBarRef.current && !mapSearchBarRef.current.contains(event.target)) {
        cancelCustomMode();
      }
    };
    document.addEventListener('gmp-click', globalClickListener);
    document.addEventListener('touchstart', globalClickListener);
    clickListener = googleMap.addListener('click', (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setCustomLatLng({ lat, lng });
      mouseMoveListener.remove();
      clickListener.remove();
      document.removeEventListener('gmp-click', globalClickListener);
      document.removeEventListener('touchstart', globalClickListener);
      if (customMarker) {
        customMarker.setMap(null);
        if (customCompositeKey) {
          markersRef.current.delete(customCompositeKey);
        }
        setCustomMarker(null);
        setCustomCompositeKey(null);
      }
      googleMap.setOptions({ draggableCursor: null });
      setIsCreatingCustom(false);
      setShowCreateCustom(true);
      setIsCustomMode(false);
    });
  };

  const cancelCustomMode = () => {
    if (!googleMap || !googleMapsAPI) return;
    setIsCreatingCustom(false);
    setShowCreateCustom(false);
    setIsCustomMode(false);
    googleMap.setOptions({ draggableCursor: null });
  };

  useEffect(() => {
    if (!isCustomMode) return;
    const handleClickOutside = (event) => {
      if (mapSearchBarRef.current && !mapSearchBarRef.current.contains(event.target)) {
        cancelCustomMode();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isCustomMode]);

  return (
    <>
      {isCreatingCustom && !isMobile && <div className="grey-out-overlay"></div>}
      <div className="map-search-bar-container" ref={mapSearchBarRef}>
        <div className="map-search-bar-top">
          <div className="map-search-bar-search-box">
            <input
              type="text"
              placeholder="Search places..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyPress}
              className="map-search-bar-search-input"
            />
            {searchTerm && (
              <button
                onClick={handleClearSearchMarkers}
                className="map-search-bar-clear-button"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        <div className="map-search-bar-type-filter">
          <button
            onClick={() => handleButtonClick('Restaurants')}
            className="map-search-bar-button"
          >
            Restaurants
          </button>
          <button
            onClick={() => handleButtonClick('Museums')}
            className="map-search-bar-button"
          >
            Museums
          </button>
          <button
            onClick={() => handleButtonClick('Hotels')}
            className="map-search-bar-button"
          >
            Hotels
          </button>
          <button
            onClick={() => handleButtonClick('Cafes')}
            className="map-search-bar-button"
          >
            Cafes
          </button>
          <button
            className={`map-search-bar-button map-search-bar-custom-button ${isCustomMode ? 'active' : ''}`}
            onClick={handleCustomButtonClick}
            onMouseEnter={() => setCustomButtonHovered(true)}
            onMouseLeave={() => setCustomButtonHovered(false)}
            aria-label="Add Custom Waypoint"
          >
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path fill="currentColor" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>
            </svg>
          </button>
        </div>
        {(isCreatingCustom || customButtonHovered) && (
          <div className="create-custom-instruction">
            {isCreatingCustom ? "Click on the map to place your custom marker." : "Create Your Own Place"}
          </div>
        )}
        {showCreateCustom && customLatLng && (
          <CreateCustomPlaceWidget
            onClose={() => {
              setShowCreateCustom(false);
              setCustomLatLng(null);
              setIsCustomMode(false);
            }}
            latitude={customLatLng.lat}
            longitude={customLatLng.lng}
          />
        )}
      </div>
    </>
  );
};

export default MapSearchBar;
